import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Loader, Upload } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';

const API = 'https://tradebot-k4nt.onrender.com/api';

// ─── Shared form field components ────────────────────────────────────────────
// IMPORTANT: these must live OUTSIDE the ConnectAccount component. When they
// were defined inside the component body, React treated them as brand-new
// component types on every re-render (which happens on every keystroke,
// since typing calls setState). That made React unmount/remount the <input>
// after each character, which is why the field appeared impossible to type
// into — focus was lost after every keystroke.
const Field = ({ label, id, type = 'text', value, onChange, placeholder = '', required = true, accentColor }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
    <input
      type={type} id={id} value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder} required={required}
      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 text-slate-900"
      style={{ '--tw-ring-color': accentColor }}
    />
  </div>
);

const FileField = ({ label, id, inputRef, accept = 'image/*' }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
    <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-slate-400 bg-slate-50">
      <Upload size={20} className="text-slate-400 mb-1" />
      <span className="text-xs text-slate-500">Click to upload</span>
      <input ref={inputRef} type="file" id={id} accept={accept} className="hidden" />
    </label>
  </div>
);

const SubmitBtn = ({ children, disabled, loading, accentColor }) => (
  <button
    type="submit"
    disabled={disabled || loading}
    style={{ background: accentColor }}
    className="w-full py-3 rounded-xl text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-2 transition-opacity"
  >
    {loading
      ? <><Loader size={16} className="animate-spin" /> Processing…</>
      : children}
  </button>
);

const BackBtn = ({ inforType, onBack }) => (
  <button
    type="button"
    onClick={() => onBack(inforType)}
    className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 text-sm font-medium"
  >
    <ArrowLeft size={16} /> Back
  </button>
);

// ─── Mirror of index.js getAccountStatus() ───────────────────────────────────
const getAccountStatus = async (username) => {
  const res = await fetch(`${API}/payment/retive-stripe-account-info/web/${username}`);
  return res.json();
};

// ─── Mirror of index.js retriveAccountLocalUpdate() for balance ───────────────
// GET /api/users/{username} → responseData.usernameInfo.accountBalance
const fetchUserBalance = async (username) => {
  const res = await fetch(`${API}/users/${username}`);
  const data = await res.json();
  return data?.usernameInfo?.accountBalance || null;
};

// ─── Determine which step to resume from based on live Stripe account data ───
// Mirrors the logic index.js uses to show/hide panels after each submission
const deriveResumeStep = (stripeAccount) => {
  // value === 2 means no account created yet → start from personal
  if (!stripeAccount || stripeAccount === 2) return 'personal';

  const req          = stripeAccount.requirements        || {};
  // eslint-disable-next-line no-unused-vars
  const futureReq    = stripeAccount.future_requirements || {};
  const ind          = stripeAccount.individual          || {};
  const extAccounts  = stripeAccount.external_accounts?.total_count || 0;
  const pastDue      = req.past_due        || [];
  const currentlyDue = req.currently_due   || [];
  const eventuallyDue= req.eventually_due  || [];
  const allDue       = [...new Set([...pastDue, ...currentlyDue, ...eventuallyDue])];

  // No bank account linked yet and it's required
  if (extAccounts === 0) {
    // Check SSN for USD
    const needId  = allDue.includes('individual.id_number');
    const needSsn = allDue.includes('individual.ssn_last_4');
    if (needId && needSsn) return 'ssn';

    // Check address
    const needAddress = allDue.some(d =>
      d === 'individual.address.city' ||
      d === 'individual.address.line1' ||
      d === 'individual.address.postal_code' ||
      d === 'individual.address.state'
    );
    if (needAddress) {
      // No personal info yet?
      if (!ind.first_name && !ind.first_name_kanji) return 'personal';
      return 'address';
    }

    // Check if doc upload needed: front=null means not uploaded
    const docFront = ind?.verification?.document?.front;
    if (docFront === null) return 'upload';

    // Has address, has doc, needs bank account
    return 'account';
  }

  // Has bank account — fully done
  return 'success';
};

// ─── Currency config ───────────────────────────────────────────────────────────
const CURRENCY_CFG = {
  USD: {
    accType:  'USD',
    country:  'US',
    parentId: 'USD-ACC',
    cssClass: 'con-container-usd-acc',
    label:    'USD',
    flag:     '🇺🇸',
    // USD panels (mirrors HTML IDs)
    panels: {
      personal: 'cusdfPinfo',
      address:  'cusdfAinfo',
      upload:   'cusdfUinfo',
      ssn:      'cusdfSSNinfo',
      account:  'cusdfAccinfo',
      success:  'cusdfSuccessComplete',
    },
    steps:          ['personal','address','ssn','account'],
    hasSsn:         true,
    sortCodeLabel:  'Routing Number',
    routerLabel:    'Router Number',
    // Upload: USD is COMMENTED OUT in index.js — it reads files but the API call is commented
    // So for USD we skip the upload step and go straight from address → ssn check → account
    hasUploadStep:  false,
  },
  GBP: {
    accType:  'GDP',   // NOTE: original HTML/JS uses "GDP" not "GBP"
    country:  'GB',
    parentId: 'GDP-ACC',
    cssClass: 'con-container-gdp-acc',
    label:    'GBP',
    flag:     '🇬🇧',
    panels: {
      personal: 'cgdpfPinfo',
      address:  'cgdpfAinfo',
      upload:   'cgdpfUinfo',
      account:  'cgdpfAccinfo',
      success:  'cgdpfSuccessComplete',
    },
    steps:          ['personal','address','upload','account'],
    hasSsn:         false,
    sortCodeLabel:  'Sort Code',
    routerLabel:    'Sort Code',
    hasUploadStep:  true,
  },
  YEN: {
    accType:  'YEN',
    country:  'JP',
    parentId: 'YEN-ACC',
    cssClass: 'con-container-yen-acc',
    label:    'YEN',
    flag:     '🇯🇵',
    panels: {
      personal: 'cyenfPinfo',
      address:  'cyenfAinfo',
      upload:   'cyenfUinfo',
      account:  'cyenfAccinfo',
      success:  'cyenfSuccessComplete',
    },
    steps:          ['personal','address','upload','account'],
    hasSsn:         false,
    sortCodeLabel:  'Router Number',
    routerLabel:    'Router Number',
    hasUploadStep:  true,
  },
};

// Step label display names
const STEP_LABELS = {
  personal: 'Personal Info',
  address:  'Address',
  upload:   'Identity Docs',
  ssn:      'SSN',
  account:  'Bank Account',
  success:  'Complete',
};

export default function ConnectAccount() {
  const navigate            = useNavigate();
  const [params]            = useSearchParams();
  const { user: ctxUser, updateBalance } = useUser();

  const rawCurrency = (params.get('currency') || 'USD').toUpperCase();
  const currency    = CURRENCY_CFG[rawCurrency] ? rawCurrency : 'USD';
  const cfg         = CURRENCY_CFG[currency];

  // Which steps are shown for this currency
  const STEPS = cfg.steps;

  const [step,     setStep]     = useState(null);   // null = init loading
  const [loading,  setLoading]  = useState(false);

  // ─── doc upload mode: 'drive' (front+back) or 'id' ──────────────────────
  const [docMode,  setDocMode]  = useState('drive');

  // ── File refs (mirrors document.getElementById("fCoverXXX").files[0]) ──
  const fCoverRef  = useRef(null);
  const bCoverRef  = useRef(null);
  const idCoverRef = useRef(null);

  // ── Personal info fields ─────────────────────────────────────────────────
  const [fname,     setFname]     = useState('');
  const [lname,     setLname]     = useState('');
  const [fnameKana, setFnameKana] = useState('');  // YEN only
  const [lnameKana, setLnameKana] = useState('');  // YEN only
  const [email,     setEmail]     = useState('');
  const [phone,     setPhone]     = useState('');
  const [dob,       setDob]       = useState('');

  // ── Address fields ───────────────────────────────────────────────────────
  const [addrCity,  setAddrCity]  = useState('');
  const [addrLoc,   setAddrLoc]   = useState('');   // line1
  const [addrState, setAddrState] = useState('');
  const [postCode,  setPostCode]  = useState('');
  // YEN extra fields (IDs match HTML exactly)
  const [blockBuildingNumber,    setBlockBuildingNumber]    = useState(''); // address_kana line1
  const [buildingNameUnitNum,    setBuildingNameUnitNum]    = useState(''); // address_kanji town
  const [buildingNameUnitNumJP,  setBuildingNameUnitNumJP]  = useState(''); // address_kana town

  // ── SSN fields (USD only) ────────────────────────────────────────────────
  const [ssnLast4,  setSsnLast4]  = useState('');
  const [idNumSSN,  setIdNumSSN]  = useState('');

  // ── Account fields ───────────────────────────────────────────────────────
  const [rNum,         setRNum]         = useState('');
  const [aNum,         setANum]         = useState('');
  const [accHoldName,  setAccHoldName]  = useState(''); // YEN only

  // ── Card fields ──────────────────────────────────────────────────────────
  const [cardNum,   setCardNum]   = useState('');
  const [cardMonth, setCardMonth] = useState('');
  const [cardYear,  setCardYear]  = useState('');
  const [cardCVC,   setCardCVC]   = useState('');

  // ── Sub-tab on account step: 'bank' | 'card' ────────────────────────────
  const [accTab, setAccTab] = useState('bank');

  // ──────────────────────────────────────────────────────────────────────────
  // On mount: fetch Stripe status → determine resume step
  // ──────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!ctxUser?.telegramName) { navigate('/login'); return; }

    (async () => {
      try {
        const data     = await getAccountStatus(ctxUser.telegramName);
        const key      = currency === 'GBP' ? 'retrieveGBP'
                       : currency === 'YEN' ? 'retrieveYEN'
                       : 'retrieveUSD';
        const acctData = data?.allAccountObj?.[key];

        // If already fully approved
        if (acctData && acctData !== 2 && acctData.charges_enabled && acctData.payouts_enabled) {
          setStep('success');
          return;
        }

        const resume = deriveResumeStep(acctData);
        // For USD, upload step is commented out in index.js; skip it
        if (resume === 'upload' && !cfg.hasUploadStep) {
          setStep('ssn');
        } else {
          setStep(resume);
        }

        // Pre-fill email if we have it
        if (ctxUser.email) setEmail(ctxUser.email);

      } catch {
        setStep('personal');
      }
    })();
  }, [ctxUser, currency, cfg.hasUploadStep, navigate]);

  // ──────────────────────────────────────────────────────────────────────────
  // Error handler — mirrors handelErrorView
  // ──────────────────────────────────────────────────────────────────────────
  const handleApiError = (dataOrMessage) => {
    const msg = typeof dataOrMessage === 'string'
      ? dataOrMessage
      : (dataOrMessage?.error?.raw?.message || 'An error occurred');
    toast.error(msg);
  };

  // ──────────────────────────────────────────────────────────────────────────
  // FUNCTION 1: handelSubmitPersonalInfo
  // Exact port of index.js lines 1376–1744
  // ──────────────────────────────────────────────────────────────────────────
  const handelSubmitPersonalInfo = async (e) => {
    e.preventDefault();
    setLoading(true);

    // eslint-disable-next-line no-unused-vars
    const accType = cfg.accType; // "USD" | "GDP" | "YEN"

    try {
      // ── STEP A: register-account-web ───────────────────────────────────
      const regBody = accType === 'USD'
        ? { email, username: ctxUser.telegramName, country: 'US' }
        : accType === 'GDP'
        ? { email, username: ctxUser.telegramName, country: 'GB' }
        : { email, username: ctxUser.telegramName, country: 'JP' };

      const regRes  = await fetch(`${API}/payment/register-account-web`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(regBody),
      });
      const regData = await regRes.json();

      if (regRes.status !== 200) { handleApiError(regData); setLoading(false); return; }

      // ── STEP B: update-account-web ─────────────────────────────────────
      let updateBody;

      if (accType === 'USD') {
        updateBody = {
          email,
          username: ctxUser.telegramName,
          country: 'US',
          businessType: 'individual',
          business_profile: { mcc: '6012', url: 'https://modanmic-fintech.onrender.com' },
          individual: {
            first_name: fname,
            last_name:  lname,
            dob: {
              day:   Number(dob.split('-')[2]),
              month: Number(dob.split('-')[1]),
              year:  Number(dob.split('-')[0]),
            },
            phone,
          },
        };
      } else if (accType === 'GDP') {
        updateBody = {
          email,
          username: ctxUser.telegramName,
          country: 'GB',
          businessType: 'individual',
          business_profile: { mcc: '6012', url: 'https://modanmic-fintech.onrender.com' },
          individual: {
            first_name: fname,
            last_name:  lname,
            dob: {
              day:   Number(dob.split('-')[2]),
              month: Number(dob.split('-')[1]),
              year:  Number(dob.split('-')[0]),
            },
            phone,
          },
        };
      } else {
        // YEN — uses kanji/kana names + product_description
        updateBody = {
          email,
          username: ctxUser.telegramName,
          country: 'JP',
          businessType: 'individual',
          business_profile: {
            mcc: '6012',
            url: 'https://modanmic-fintech.onrender.com',
            product_description: 'MODAMIC Fintech、ソフトウェア製品は、海外での送金と受け取り、暗号通貨の送受信、暗号通貨の購入と交換を行い、ストアの支払いリンクを作成してビジネスに統合することもできます。',
          },
          individual: {
            first_name_kanji: fname,
            first_name_kana:  fnameKana,
            last_name_kanji:  lname,
            last_name_kana:   lnameKana,
            dob: {
              day:   Number(dob.split('-')[2]),
              month: Number(dob.split('-')[1]),
              year:  Number(dob.split('-')[0]),
            },
            phone,
          },
        };
      }

      const updRes  = await fetch(`${API}/payment/update-account-web`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(updateBody),
      });
      const updData = await updRes.json();

      if (updRes.status !== 200) { handleApiError(updData); setLoading(false); return; }

      // On success: show address panel (exact mirror of DOM manipulation)
      setStep('address');
      toast.success('Personal info saved');

    } catch {
      handleApiError('NETWORK ERROR');
    } finally {
      setLoading(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // FUNCTION 2: handelSubmitAddresslInfo
  // Exact port of index.js lines 1746–2078
  // ──────────────────────────────────────────────────────────────────────────
  const handelSubmitAddresslInfo = async (e) => {
    e.preventDefault();
    setLoading(true);

    const accType = cfg.accType;

    try {
      let addrBody;

      if (accType === 'USD') {
        // index.js line 1771–1785
        addrBody = {
          username: ctxUser.telegramName,
          country: 'US',
          individual: {
            address: {
              city:        addrCity,
              country:     'US',
              line1:       addrLoc,
              line2:       null,
              postal_code: postCode,
              state:       addrState,
            },
          },
        };
      } else if (accType === 'GDP') {
        // index.js line 1878–1893
        addrBody = {
          username: ctxUser.telegramName,
          country: 'GB',
          individual: {
            address: {
              city:        addrCity,
              country:     'GB',
              line1:       addrLoc,
              line2:       null,
              postal_code: postCode,
              state:       addrState,
            },
          },
        };
      } else {
        // YEN — index.js line 1984–2008
        addrBody = {
          username: ctxUser.telegramName,
          country: 'JP',
          individual: {
            address_kanji: {
              city:        addrCity,
              country:     'JP',
              line1:       addrLoc,
              town:        buildingNameUnitNum,
              postal_code: postCode,
              state:       addrState,
            },
            address_kana: {
              line1: blockBuildingNumber,
              town:  buildingNameUnitNumJP,
            },
          },
          company: {
            address_kana: {
              line1: blockBuildingNumber,
            },
          },
        };
      }

      const res  = await fetch(`${API}/payment/update-account-web/address`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(addrBody),
      });
      const data = await res.json();

      if (res.status !== 200) { handleApiError(data); setLoading(false); return; }

      // Post-address: call getAccountStatus() to decide next panel
      // Exact mirror of index.js post-address logic
      const retiveaccountData = await getAccountStatus(ctxUser.telegramName);

      if (accType === 'USD') {
        // index.js line 1804–1834
        const skipSsnCondition =
          retiveaccountData.allAccountObj.retrieveUSD >= 0
            ? 0
            : retiveaccountData.allAccountObj.retrieveUSD.requirements.eventually_due;

        const idVerf  = !skipSsnCondition.length ? [] : skipSsnCondition.filter(v => v === 'individual.id_number');
        const ssnVerf = !skipSsnCondition.length ? [] : skipSsnCondition.filter(v => v === 'individual.ssn_last_4');

        if (idVerf[0] && ssnVerf[0]) {
          // USD: SSN needed — but USD upload is commented out, so go SSN then account
          setStep('ssn');
        } else {
          // USD: no SSN needed
          setStep('account');
        }

      } else if (accType === 'GDP') {
        // index.js line 1912–1940
        const skipAddCondition =
          retiveaccountData.allAccountObj.retrieveGBP >= 0
            ? {}
            : retiveaccountData.allAccountObj.retrieveGBP.individual.verification.document;

        const getObjNames        = Object.keys(skipAddCondition);
        const frontCoverCondition = getObjNames.filter(v => v === 'front');

        if (skipAddCondition[frontCoverCondition[0]] === null) {
          // front is null → doc not uploaded yet → show upload
          setStep('upload');
        } else {
          setStep('account');
        }

      } else {
        // YEN — index.js line 2025–2054
        const skipAddCondition =
          retiveaccountData.allAccountObj.retrieveYEN >= 0
            ? {}
            : retiveaccountData.allAccountObj.retrieveYEN.individual.verification.document;

        const getObjNames        = Object.keys(skipAddCondition);
        const frontCoverCondition = getObjNames.filter(v => v === 'front');

        if (skipAddCondition[frontCoverCondition[0]] === null) {
          setStep('upload');
        } else {
          setStep('account');
        }
      }

      toast.success('Address saved');

    } catch {
      handleApiError('NETWORK ERROR');
    } finally {
      setLoading(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // FUNCTION 3: handelSubmitUploadlInfo
  // Exact port of index.js lines 2080–2529
  // NOTE: USD upload is entirely commented out in index.js — we skip it for USD
  // GBP/YEN: FormData, NO Content-Type header (browser sets boundary)
  //   front+back → /upload/front-back-corver-gbp  (both GBP and YEN use -gbp endpoint)
  //   id only    → /upload/id-cover-gbp            (both GBP and YEN use -gbp endpoint)
  //   FormData keys: fCoverGDP / bCoverGDP / idCoverGDP (YEN also uses GDP keys per index.js)
  // ──────────────────────────────────────────────────────────────────────────
  const handelSubmitUploadlInfo = async (e) => {
    e.preventDefault();
    setLoading(true);

    const accType  = cfg.accType; // 'GDP' or 'YEN'
    const country  = cfg.country; // 'GB' or 'JP'

    const fFile  = fCoverRef.current?.files[0];
    const bFile  = bCoverRef.current?.files[0];
    const idFile = idCoverRef.current?.files[0];

    const fVal   = fCoverRef.current?.value  || '';
    const bVal   = bCoverRef.current?.value  || '';

    try {
      if (docMode === 'drive' && fVal && bVal) {
        // ── Front + Back (GBP index.js line 2249–2293, YEN line 2393–2437) ──
        const formData = new FormData();
        formData.append('fCoverGDP', fFile);  // YEN also uses 'fCoverGDP' key (per index.js line 2394)
        formData.append('bCoverGDP', bFile);  // YEN also uses 'bCoverGDP' key (per index.js line 2395)
        formData.append('username', ctxUser.telegramName);
        formData.append('country', country);

        // Both GBP and YEN use front-back-corver-gbp endpoint (per index.js lines 2270, 2414)
        const res  = await fetch(`${API}/payment/update-account-web/upload/front-back-corver-gbp`, {
          method: 'POST',
          // NO Content-Type header — browser sets multipart/form-data with boundary
          body: formData,
        });
        const data = await res.json();

        if (res.status !== 200) { handleApiError(data); setLoading(false); return; }

        // On success: show account panel
        setStep('account');
        toast.success('Documents uploaded');

      } else {
        // ── ID card only (GBP index.js line 2314–2356, YEN line 2458–2503) ──
        if (!idFile) { toast.error('Please select a document to upload'); setLoading(false); return; }

        const formDataID = new FormData();
        formDataID.append('idCoverGDP', idFile); // YEN also uses 'idCoverGDP' key (per index.js line 2459)
        formDataID.append('username', ctxUser.telegramName);
        formDataID.append('country', country);

        // Both GBP and YEN use id-cover-gbp endpoint (per index.js lines 2334, 2478)
        const res  = await fetch(`${API}/payment/update-account-web/upload/id-cover-gbp`, {
          method: 'POST',
          // NO Content-Type header
          body: formDataID,
        });
        const data = await res.json();

        if (res.status !== 200) { handleApiError(data); setLoading(false); return; }

        setStep('account');
        toast.success('Document uploaded');
      }

    } catch {
      handleApiError('NETWORK ERROR');
    } finally {
      setLoading(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // FUNCTION 4: handelSubmitSSNlInfo
  // Exact port of index.js lines 2531–2627 (USD only)
  // GBP/GDP and YEN just navigate to account (no API call needed)
  // ──────────────────────────────────────────────────────────────────────────
  const handelSubmitSSNlInfo = async (e) => {
    e.preventDefault();
    setLoading(true);

    const accType = cfg.accType;

    if (accType === 'USD') {
      // index.js line 2550–2597
      try {
        const res  = await fetch(`${API}/payment/update-account-web/ssn`, {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: ctxUser.telegramName,
            country: 'US',
            individual: {
              ssn_last_4: ssnLast4,
              id_number:  idNumSSN,
            },
          }),
        });
        const data = await res.json();

        if (res.status !== 200) { handleApiError(data); setLoading(false); return; }

        // On success: show account panel
        setStep('account');
        toast.success('SSN saved');

      } catch {
        handleApiError('NETWORK ERROR');
      } finally {
        setLoading(false);
      }

    } else if (accType === 'GDP') {
      // index.js line 2611–2616: just shows account panel, no API call
      setStep('account');
      setLoading(false);

    } else {
      // YEN — index.js line 2617–2622: just shows account panel, no API call
      setStep('account');
      setLoading(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // FUNCTION 5: handelSubmitAcclInfo
  // Exact port of index.js lines 2629–2853
  // ──────────────────────────────────────────────────────────────────────────
  const handelSubmitAcclInfo = async (e) => {
    e.preventDefault();
    setLoading(true);

    const accType = cfg.accType;

    try {
      let body;

      if (accType === 'USD') {
        // index.js line 2647–2659
        body = {
          username:      ctxUser.telegramName,
          country:       'US',
          accountNumber: aNum,
          routingNumber: rNum,
        };
      } else if (accType === 'GDP') {
        // index.js line 2717–2729
        body = {
          username:      ctxUser.telegramName,
          country:       'GB',
          accountNumber: aNum,
          routingNumber: rNum,
        };
      } else {
        // YEN — index.js line 2790–2803 (includes accHoldNameYEN)
        body = {
          username:       ctxUser.telegramName,
          country:        'JP',
          accountNumber:  aNum,
          routingNumber:  rNum,
          accHoldNameYEN: accHoldName,
        };
      }

      const res  = await fetch(`${API}/payment/add-account-number/web`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (res.status !== 200) { handleApiError(data); setLoading(false); return; }

      // On success: show success panel + refresh balance
      const newBalance = await fetchUserBalance(ctxUser.telegramName);
      if (newBalance) updateBalance(newBalance);

      setStep('success');
      toast.success(`${cfg.label} account connected! 🎉`);

    } catch {
      handleApiError('NETWORK ERROR');
    } finally {
      setLoading(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // FUNCTION 6: handelSubmitCardlInfo
  // Exact port of index.js lines 2855–3091
  // ──────────────────────────────────────────────────────────────────────────
  const handelSubmitCardlInfo = async (e) => {
    e.preventDefault();
    setLoading(true);

    const accType = cfg.accType;

    try {
      let body;

      if (accType === 'USD') {
        // index.js line 2883–2891
        body = {
          username:   ctxUser.telegramName,
          country:    'US',
          exp_month:  cardMonth,
          exp_year:   cardYear,
          cardNumber: cardNum,
          cvc:        cardCVC,
        };
      } else if (accType === 'GDP') {
        // index.js line 2957–2965
        body = {
          username:   ctxUser.telegramName,
          country:    'GB',
          exp_month:  cardMonth,
          exp_year:   cardYear,
          cardNumber: cardNum,
          cvc:        cardCVC,
        };
      } else {
        // YEN — index.js line 3033–3041
        body = {
          username:   ctxUser.telegramName,
          country:    'JP',
          exp_month:  cardMonth,
          exp_year:   cardYear,
          cardNumber: cardNum,
          cvc:        cardCVC,
        };
      }

      const res  = await fetch(`${API}/payment/add-card-payout-web`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (res.status !== 200) { handleApiError(data); setLoading(false); return; }

      // On success: show success panel + refresh balance
      const newBalance = await fetchUserBalance(ctxUser.telegramName);
      if (newBalance) updateBalance(newBalance);

      setStep('success');
      toast.success('Card linked! 🎉');

    } catch {
      handleApiError('NETWORK ERROR');
    } finally {
      setLoading(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // BACK NAVIGATION — mirrors handelBackInfrMation in index.js
  // ──────────────────────────────────────────────────────────────────────────
  const handelBackInfrMation = (inforType) => {
    const accType = cfg.accType;

    if (accType === 'USD') {
      if (inforType === 'add')    setStep('personal');
      else if (inforType === 'upload') setStep('address');
      else if (inforType === 'ssn')    setStep('address');
      else                             setStep('ssn');  // 'none' → back to SSN

    } else if (accType === 'GDP') {
      if (inforType === 'add')    setStep('personal');
      else if (inforType === 'upload') setStep('address');
      else                             setStep('upload'); // 'none' → back to upload

    } else {
      // YEN
      if (inforType === 'add')    setStep('personal');
      else if (inforType === 'upload') setStep('address');
      else                             setStep('upload'); // 'none' → back to upload
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // UI: stepper bar
  // ──────────────────────────────────────────────────────────────────────────
  const gradMap = { USD: '#16a34a', GBP: '#2563eb', YEN: '#dc2626' };
  const accentColor = gradMap[currency];

  const stepIdx = step ? STEPS.indexOf(step) : -1;

  const Stepper = () => (
    <div className="flex items-center mb-6 px-1">
      {STEPS.map((s, i) => {
        const done    = i < stepIdx;
        const current = i === stepIdx;
        return (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center flex-shrink-0">
              <div
                style={{ background: (done || current) ? accentColor : '#e2e8f0', color: (done || current) ? '#fff' : '#94a3b8' }}
                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all"
              >
                {done ? '✓' : i + 1}
              </div>
              <span className={`text-xs mt-1 text-center leading-tight ${current ? 'font-semibold text-slate-800' : 'text-slate-400'}`}
                style={{ maxWidth: 52 }}>
                {STEP_LABELS[s]}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className="flex-1 h-0.5 mx-1 mb-4"
                style={{ background: done ? accentColor : '#e2e8f0', transition: 'background 0.3s' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  // ──────────────────────────────────────────────────────────────────────────
  // LOADING STATE
  // ──────────────────────────────────────────────────────────────────────────
  if (step === null) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Loader className="animate-spin h-12 w-12 mx-auto mb-3" style={{ color: accentColor }} />
            <p className="text-slate-500">Loading account status…</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  // SUCCESS PANEL — mirrors cusdfSuccessComplete / cgdpfSuccessComplete / cyenfSuccessComplete
  // ──────────────────────────────────────────────────────────────────────────
  if (step === 'success') {
    return (
      <DashboardLayout>
        <div
          id={cfg.parentId}
          className={`${cfg.cssClass} max-w-md mx-auto px-4 py-16 text-center`}
        >
          <div id={cfg.panels.success} className="continer-completed-account">
            <div
              style={{ background: accentColor }}
              className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle className="text-white" size={48} />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              {cfg.label} Account Connected!
            </h2>
            <p className="text-slate-500 mb-8">
              Your account has been successfully set up and is pending review.
            </p>
            <button
              onClick={() => navigate('/account-verification')}
              style={{ background: accentColor }}
              className="px-8 py-3 rounded-xl text-white font-bold"
            >
              View Status
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  // MAIN FLOW
  // ──────────────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      {/* Outer wrapper — ID and class names match HTML exactly */}
      <div
        id={cfg.parentId}
        className={`${cfg.cssClass} max-w-2xl mx-auto px-4 sm:px-6 py-8`}
      >
        {/* Page header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/account-verification')}
            className="text-slate-500 hover:text-slate-800"
          >
            <ArrowLeft size={22} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">
              {cfg.flag} Connect {cfg.label} Account
            </h1>
            <p className="text-sm text-slate-500">
              Step {stepIdx + 1} of {STEPS.length} — {STEP_LABELS[step]}
            </p>
          </div>
        </div>

        <Stepper />

        {/* ──────────────────────────────────────────────────────────────────
            STEP: PERSONAL INFO
            HTML panel IDs: cusdfPinfo / cgdpfPinfo / cyenfPinfo
            onsubmit: handelSubmitPersonalInfo(event, '{accType}')
        ────────────────────────────────────────────────────────────────── */}
        {step === 'personal' && (
          <div id={cfg.panels.personal} className="con-USD-form usd-personal-info-container bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <h5 className="font-semibold text-slate-900 text-base mb-4">Personal Information</h5>
            <form onSubmit={handelSubmitPersonalInfo} className="usd-personal-info space-y-4">

              {currency === 'YEN' ? (
                <>
                  <Field label="First Name (Kanji)" id={`fname${currency}`}     value={fname}     onChange={setFname}     placeholder="太郎" accentColor={accentColor} />
                  <Field label="Last Name (Kanji)"  id={`lname${currency}`}     value={lname}     onChange={setLname}     placeholder="山田" accentColor={accentColor} />
                  <Field label="First Name (KANA)"  id={`fname${currency}Kana`} value={fnameKana} onChange={setFnameKana} placeholder="タロウ" accentColor={accentColor} />
                  <Field label="Last Name (KANA)"   id={`lname${currency}Kana`} value={lnameKana} onChange={setLnameKana} placeholder="ヤマダ" accentColor={accentColor} />
                </>
              ) : (
                <>
                  {/* IDs match HTML: fnameUSD / fnameGDP */}
                  <Field label="First Name" id={`fname${cfg.accType}`} value={fname} onChange={setFname} placeholder="John" accentColor={accentColor} />
                  <Field label="Last Name"  id={`lname${cfg.accType}`} value={lname} onChange={setLname} placeholder="Doe" accentColor={accentColor} />
                </>
              )}

              <Field label="Email"        type="email" id={`email${cfg.accType}`} value={email} onChange={setEmail} placeholder="you@email.com" accentColor={accentColor} />
              <Field label="Phone Number" type="tel"   id={`phone${cfg.accType}`} value={phone} onChange={setPhone} placeholder="+1 555 0000" accentColor={accentColor} />
              <Field label="Date of Birth" type="date" id={`dob${cfg.accType}`}   value={dob}   onChange={setDob} accentColor={accentColor} />

              <SubmitBtn loading={loading} accentColor={accentColor}>Next</SubmitBtn>
            </form>
          </div>
        )}

        {/* ──────────────────────────────────────────────────────────────────
            STEP: ADDRESS
            HTML panel IDs: cusdfAinfo / cgdpfAinfo / cyenfAinfo
            onsubmit: handelSubmitAddresslInfo(event, '{accType}')
            back: handelBackInfrMation('add', '{accType}')
        ────────────────────────────────────────────────────────────────── */}
        {step === 'address' && (
          <div id={cfg.panels.address} className="con-USD-form usd-address-info-container bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <h5 className="font-semibold text-slate-900 text-base mb-4">Address Information</h5>
            <form onSubmit={handelSubmitAddresslInfo} className="usd-personal-info space-y-4">
              <BackBtn inforType="add" onBack={handelBackInfrMation} />

              {/* City — id: addressCityUSD / addressCityGDP / addressCityYEN */}
              <Field label="City Address" id={`addressCity${cfg.accType}`} value={addrCity}  onChange={setAddrCity}  placeholder="Los Angeles" accentColor={accentColor} />

              {currency === 'YEN' && (
                /* buildingNameUnitNum — Address Town (Kanji) — index.js line 1970 */
                <Field label="Address Town" id="buildingNameUnitNum" value={buildingNameUnitNum} onChange={setBuildingNameUnitNum} placeholder="Address Town" accentColor={accentColor} />
              )}

              {/* Line 1 — id: addressLocUSD / addressLocGDP / addressLocYEN */}
              <Field label="Address Location" id={`addressLoc${cfg.accType}`} value={addrLoc} onChange={setAddrLoc} placeholder="123 Main St" accentColor={accentColor} />

              {currency === 'YEN' && (
                /* blockBuildingNumber — Address Kana Location — index.js line 1968 */
                <Field label="Address Kana Location" id="blockBuildingNumber" value={blockBuildingNumber} onChange={setBlockBuildingNumber} placeholder="Address Kana Location" accentColor={accentColor} />
              )}

              {currency === 'YEN' && (
                /* buildingNameUnitNumJP — Address Kana Town — index.js line 1969 */
                <Field label="Address Kana Town (katakana)" id="buildingNameUnitNumJP" value={buildingNameUnitNumJP} onChange={setBuildingNameUnitNumJP} placeholder="Address Kana Town" accentColor={accentColor} />
              )}

              {/* State — id: addressStateUSD / addressStateGDP / addressStateYEN */}
              <Field label="State Address" id={`addressState${cfg.accType}`} value={addrState} onChange={setAddrState} placeholder="CA" accentColor={accentColor} />

              {/* Post Code — id: postCodeUSD / postCodeGDP / postCodeYEN */}
              <Field label="Post Code" id={`postCode${cfg.accType}`} value={postCode} onChange={setPostCode} placeholder="90001" accentColor={accentColor} />

              <SubmitBtn loading={loading} accentColor={accentColor}>Next</SubmitBtn>
            </form>
          </div>
        )}

        {/* ──────────────────────────────────────────────────────────────────
            STEP: UPLOAD IDENTITY (GBP + YEN only — USD is commented out)
            HTML panel IDs: cusdfUinfo / cgdpfUinfo / cyenfUinfo
            onsubmit: handelSubmitUploadlInfo(event, '{accType}')
            back: handelBackInfrMation('upload', '{accType}')
            Drive section: cudGDP / cudYEN  with fCoverGDP/bCoverGDP
            ID section:    ciIDGDP / ciIDYEN with idCoverGDP
        ────────────────────────────────────────────────────────────────── */}
        {step === 'upload' && cfg.hasUploadStep && (
          <div id={cfg.panels.upload} className="con-USD-form usd-upload-info-container bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <h5 className="font-semibold text-slate-900 text-base mb-4">Upload Identity Information</h5>
            <form onSubmit={handelSubmitUploadlInfo} className="usd-personal-info space-y-4">
              <BackBtn inforType="upload" onBack={handelBackInfrMation} />

              {/* Toggle driver vs ID */}
              <div className="flex rounded-xl border border-slate-200 overflow-hidden mb-2">
                <button type="button"
                  onClick={() => setDocMode('drive')}
                  style={{ background: docMode === 'drive' ? accentColor : '', color: docMode === 'drive' ? '#fff' : '' }}
                  className={`flex-1 py-2 text-sm font-medium transition-all ${docMode !== 'drive' ? 'text-slate-600 hover:bg-slate-50' : ''}`}
                >
                  Driver's Licence
                </button>
                <button type="button"
                  onClick={() => setDocMode('id')}
                  style={{ background: docMode === 'id' ? accentColor : '', color: docMode === 'id' ? '#fff' : '' }}
                  className={`flex-1 py-2 text-sm font-medium transition-all ${docMode !== 'id' ? 'text-slate-600 hover:bg-slate-50' : ''}`}
                >
                  ID Card
                </button>
              </div>

              {/* Driver section — id: cudGDP / cudYEN */}
              {docMode === 'drive' && (
                <div id={`cud${cfg.accType}`} className="continer-upload-driv space-y-3">
                  {/* fCoverGDP / fCoverYEN */}
                  <FileField label="Front Cover" id={`fCover${cfg.accType}`} inputRef={fCoverRef} />
                  {/* bCoverGDP / bCoverYEN */}
                  <FileField label="Back Cover"  id={`bCover${cfg.accType}`} inputRef={bCoverRef} />
                  <button type="button" onClick={() => setDocMode('id')} className="text-sm underline" style={{ color: accentColor }}>
                    I do not have a driver's licence
                  </button>
                </div>
              )}

              {/* ID section — id: ciIDGDP / ciIDYEN */}
              {docMode === 'id' && (
                <div id={`ciID${cfg.accType}`} className="continer-upload-ID space-y-3">
                  {/* idCoverGDP / idCoverYEN */}
                  <FileField label="Identification Card" id={`idCover${cfg.accType}`} inputRef={idCoverRef} />
                  <button type="button" onClick={() => setDocMode('drive')} className="text-sm underline" style={{ color: accentColor }}>
                    I do not have an identification card
                  </button>
                </div>
              )}

              <SubmitBtn loading={loading} accentColor={accentColor}>Next</SubmitBtn>
            </form>
          </div>
        )}

        {/* ──────────────────────────────────────────────────────────────────
            STEP: SSN (USD only)
            HTML panel ID: cusdfSSNinfo
            onsubmit: handelSubmitSSNlInfo(event, 'USD')
            back: handelBackInfrMation('ssn', 'USD')
        ────────────────────────────────────────────────────────────────── */}
        {step === 'ssn' && (
          <div id="cusdfSSNinfo" className="con-USD-form usd-ssn-info-container bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <h5 className="font-semibold text-slate-900 text-base mb-4">SSN Last 4 Digits</h5>
            <form onSubmit={handelSubmitSSNlInfo} className="usd-personal-info space-y-5">
              <BackBtn inforType="ssn" onBack={handelBackInfrMation} />

              <div className="container-input ssn-upload">
                <label htmlFor="ssnUSD" className="block text-sm font-medium text-slate-700 mb-1 text-center">SSN Last 4</label>
                <input
                  type="number" id="ssnUSD" maxLength={4}
                  value={ssnLast4} onChange={e => setSsnLast4(e.target.value)}
                  placeholder="1234" required
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 text-center text-2xl tracking-widest"
                  style={{ '--tw-ring-color': accentColor }}
                />
              </div>

              <div className="container-input ssn-upload">
                <label htmlFor="idNumUSD" className="block text-sm font-medium text-slate-700 mb-1 text-center">SSN Complete</label>
                <input
                  type="number" id="idNumUSD"
                  value={idNumSSN} onChange={e => setIdNumSSN(e.target.value)}
                  placeholder="123-45-6789" required
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 text-center"
                  style={{ '--tw-ring-color': accentColor }}
                />
              </div>

              <SubmitBtn loading={loading} accentColor={accentColor}>Submit</SubmitBtn>
            </form>
          </div>
        )}

        {/* ──────────────────────────────────────────────────────────────────
            STEP: BANK ACCOUNT + CARD
            HTML panel IDs: cusdfAccinfo / cgdpfAccinfo / cyenfAccinfo
            onsubmit bank: handelSubmitAcclInfo(event, '{accType}')
            onsubmit card: handelSubmitCardlInfo(event, '{accType}')
            back: handelBackInfrMation('none', '{accType}')
        ────────────────────────────────────────────────────────────────── */}
        {step === 'account' && (
          <div id={cfg.panels.account} className="con-USD-form usd-acc-info-container bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">

            {/* Sub-tab: Bank vs Card */}
            <div className="flex rounded-xl border border-slate-200 overflow-hidden mb-5">
              <button type="button"
                onClick={() => setAccTab('bank')}
                style={{ background: accTab === 'bank' ? accentColor : '' }}
                className={`flex-1 py-2 text-sm font-semibold transition-all ${accTab === 'bank' ? 'text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                Bank Account
              </button>
              <button type="button"
                onClick={() => setAccTab('card')}
                style={{ background: accTab === 'card' ? accentColor : '' }}
                className={`flex-1 py-2 text-sm font-semibold transition-all ${accTab === 'card' ? 'text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                Debit Card
              </button>
            </div>

            {/* ── BANK ACCOUNT FORM ── */}
            {accTab === 'bank' && (
              <form onSubmit={handelSubmitAcclInfo} className="usd-personal-info space-y-4">
                <h5 className="font-semibold text-slate-900 text-base mb-2">Add {cfg.label} Account</h5>
                <BackBtn inforType="none" onBack={handelBackInfrMation} />

                {/* YEN has Account Holder Name first */}
                {currency === 'YEN' && (
                  <Field label="Account Holders Name" id="accHoldNameYEN" value={accHoldName} onChange={setAccHoldName} placeholder="ヤマダハナコ" accentColor={accentColor} />
                )}

                {/* rNumUSD / rNumGDP / rNumYEN */}
                <Field label={cfg.routerLabel} id={`rNum${cfg.accType}`} value={rNum} onChange={setRNum} placeholder="xxxx-xxxx-xx" accentColor={accentColor} />

                {/* aNumUSD / aNumGDP / aNumYEN */}
                <Field label="Account Number" type="number" id={`aNum${cfg.accType}`} value={aNum} onChange={setANum} placeholder="0123456789" accentColor={accentColor} />

                <SubmitBtn loading={loading} accentColor={accentColor}>Add Account</SubmitBtn>
              </form>
            )}

            {/* ── CARD FORM ── */}
            {accTab === 'card' && (
              <form onSubmit={handelSubmitCardlInfo} className="space-y-4">
                <h5 className="font-semibold text-slate-900 text-base mb-2">Add Debit Card</h5>
                <BackBtn inforType="none" onBack={handelBackInfrMation} />

                {/* cardNumUSD / cardNumGDP / cardNumYEN */}
                <Field label="Card Number" id={`cardNum${cfg.accType}`} value={cardNum} onChange={setCardNum} placeholder="4242 4242 4242 4242" accentColor={accentColor} />

                <div className="grid grid-cols-3 gap-3">
                  {/* cardMonthUSD / cardMonthGDP / cardMonthYEN */}
                  <Field label="Exp Month" id={`cardMonth${cfg.accType}`} type="number" value={cardMonth} onChange={setCardMonth} placeholder="MM" accentColor={accentColor} />
                  {/* cardYearUSD / cardYearGDP / cardYearYEN */}
                  <Field label="Exp Year"  id={`cardYear${cfg.accType}`}  type="number" value={cardYear}  onChange={setCardYear}  placeholder="YYYY" accentColor={accentColor} />
                  {/* cardCVCUSD / cardCVCGDP / cardCVCYEN */}
                  <Field label="CVC"       id={`cardCVC${cfg.accType}`}   type="number" value={cardCVC}   onChange={setCardCVC}   placeholder="123" accentColor={accentColor} />
                </div>

                <SubmitBtn loading={loading} accentColor={accentColor}>Add Card</SubmitBtn>
              </form>
            )}
          </div>
        )}

        <p className="text-xs text-slate-400 text-center mt-4">
          🔒 Secured by Stripe. All data encrypted in transit.
        </p>
      </div>
    </DashboardLayout>
  );
}
